import { useState, useRef, useCallback, useEffect } from "react";
import { useLogs } from "../context/LogsContext";

export const useBlockTestsSearch = () => {
  const {
    searchBlockTests,
    blockTestsPagination,
    fetchBlockTestsForPage,
    resetBlockTests,
    error,
  } = useLogs();

  // Single state update for api error to avoid re-renders
  const apiErrorRef = useRef(false);

  // Search state - kept minimal to avoid re-renders
  const [searchMode, setSearchMode] = useState("name");
  const [nameQuery, setNameQuery] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state - setup once and updated minimally
  const [effectivePagination, setEffectivePagination] =
    useState(blockTestsPagination);
  const currentSearchPageRef = useRef(1);

  // Only update pagination when required - with improved dependency management
  useEffect(() => {
    // Skip this effect when searching to prevent loops
    if (isSearching) return;

    // Skip if there's an error to prevent loops
    if (apiErrorRef.current) return;

    // Only update if actually different to avoid re-render loops
    if (
      JSON.stringify(effectivePagination) !==
      JSON.stringify(blockTestsPagination)
    ) {
      setEffectivePagination(blockTestsPagination);
    }
  }, [blockTestsPagination]);

  // Track error state with ref to avoid unnecessary re-renders
  useEffect(() => {
    // Only update the ref if error state actually changed
    if (error && !apiErrorRef.current) {
      apiErrorRef.current = true;
    } else if (!error && apiErrorRef.current) {
      // Handle error state clearing
      apiErrorRef.current = false;
    }
  }, [error]);

  // Safe search mode change
  const handleSearchModeChange = useCallback(
    (mode) => {
      // Reset error state
      apiErrorRef.current = false;

      // Reset state
      setSearchMode(mode);
      setNameQuery("");
      setSelectedOrgId("");
      setIsSearching(false);

      // Clear previous state
      resetBlockTests();

      // Fetch fresh data - but only once
      fetchBlockTestsForPage(1, true);
    },
    [resetBlockTests, fetchBlockTestsForPage]
  );

  // Simple name change with error prevention
  const handleNameChange = useCallback((value) => {
    setNameQuery(value);
  }, []);

  // Simple organization selection with error prevention
  const handleOrgSelect = useCallback((value) => {
    setSelectedOrgId(value);
  }, []);

  // Get current search query - kept pure with no side effects
  const getCurrentSearchQuery = useCallback(() => {
    if (searchMode === "name" && nameQuery) {
      return nameQuery;
    } else if (searchMode === "organization" && selectedOrgId) {
      return selectedOrgId;
    }
    return null;
  }, [searchMode, nameQuery, selectedOrgId]);

  // Handle search submission - with better dependency management
  const handleSearch = useCallback(() => {
    // Breaking circular dependencies by getting values directly
    const currentQuery = getCurrentSearchQuery();
    if (!currentQuery) return;

    // Reset error state
    apiErrorRef.current = false;

    // Set searching mode
    setIsSearching(true);
    currentSearchPageRef.current = 1;

    // Prepare search params
    const searchParams = {};
    if (searchMode === "name") {
      searchParams.name = currentQuery;
    } else if (searchMode === "organization") {
      searchParams.orgid = parseInt(currentQuery, 10);
    }

    // Wrap in setTimeout to break potential circular dependencies
    setTimeout(() => {
      // Use a try-catch pattern directly to handle errors locally
      searchBlockTests(searchParams, 1, blockTestsPagination.limit)
        .then((searchData) => {
          // Only update pagination on success
          if (searchData?.pagination) {
            setEffectivePagination({
              page: 1,
              limit: blockTestsPagination.limit,
              total: searchData.pagination.total,
            });
          }
        })
        .catch((error) => {
          console.error("Error searching block tests:", error);
          apiErrorRef.current = true;
        });
    }, 0);
  }, [
    getCurrentSearchQuery,
    searchMode,
    searchBlockTests,
    blockTestsPagination.limit,
  ]);

  // Clear search with clean state reset
  const clearSearch = useCallback(() => {
    // Reset error state
    apiErrorRef.current = false;

    // Clear state
    setNameQuery("");
    setSelectedOrgId("");
    setIsSearching(false);

    // Reset context state
    resetBlockTests();

    // Fetch fresh data
    fetchBlockTestsForPage(1);
  }, [resetBlockTests, fetchBlockTestsForPage]);

  // Computed property from ref to avoid triggering re-renders
  const hasApiError = error !== null || apiErrorRef.current;

  return {
    searchMode,
    nameQuery,
    selectedOrgId,
    isSearching,
    currentSearchPageRef,
    effectivePagination,
    handleSearchModeChange,
    handleNameChange,
    handleOrgSelect,
    handleSearch,
    clearSearch,
    getCurrentSearchQuery,
    hasApiError,
  };
};
